/**
 * Field Grouping Configuration for Quick-Add Mode (Progressive Disclosure)
 *
 * Defines which fields are always visible and which go into collapsible
 * accordion sections for each asset type. Fields not listed in any group
 * will still render as a fallback at the bottom of the form.
 *
 * Structure:
 *   alwaysVisible: fields shown at the top of the form, always expanded
 *   sections: collapsible accordion groups (closed by default)
 */

const FIELD_GROUPS = {
    // ── Hydrant ──────────────────────────────────────────
    // display:true fields: ClientHydrantID, HydrantType, Color, HydrantManufacturer,
    //   HydrantManufacturedDate, IsolationValve, SnowPlowFlag, SideOfRoad,
    //   SubDivisionName, CouncilDistrict, DateInstalled, Comment
    Hydrant: {
        alwaysVisible: [
            'ClientHydrantID',
            'HydrantType',
            'Color',
            'HydrantManufacturer',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['SideOfRoad', 'SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: ['HydrantManufacturedDate', 'IsolationValve', 'SnowPlowFlag', 'DateInstalled'],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },

    // ── Manhole ──────────────────────────────────────────
    // display:true fields: ClientManholeID, ManholeModel, ManholeMaterial,
    //   ManholeManufacturer, ManholeManufacturedDate, LiftStation, MeterStation,
    //   DropManhole, CleanOut, Ladder, BottomElevation, RimElevation, Depth,
    //   DateInstalled, SubDivisionName, CouncilDistrict, Comment
    Manhole: {
        alwaysVisible: [
            'ClientManholeID',
            'ManholeModel',
            'ManholeMaterial',
            'Depth',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: [
                    'ManholeManufacturer', 'ManholeManufacturedDate',
                    'LiftStation', 'MeterStation', 'DropManhole',
                    'CleanOut', 'Ladder', 'BottomElevation', 'RimElevation',
                    'DateInstalled',
                ],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },

    // ── Valve ────────────────────────────────────────────
    // display:true fields: ClientValveID, ValveModel, ValveType, ValveManufacturer,
    //   ValveManufacturedDate, ValveHandle, MeterStation, TurnDirection, Depth,
    //   StreetName, Location, DateInstalled, SubDivisionName, CouncilDistrict, Comment
    Valve: {
        alwaysVisible: [
            'ClientValveID',
            'ValveType',
            'ValveHandle',
            'Depth',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['StreetName', 'Location', 'SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: [
                    'ValveModel', 'ValveManufacturer', 'ValveManufacturedDate',
                    'MeterStation', 'TurnDirection', 'DateInstalled',
                ],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },

    // ── TrafficSign ──────────────────────────────────────
    // display:true fields: ClientSignID, StreetName, CardinalDirection,
    //   SignClassification, MUTCDCode, SignText, PanelHeight, PanelWidth,
    //   TextHeight, MountingHeight, SupportType, SupportMaterial, SignBacking,
    //   SheetingType, SignManufacturer, SignManufacturedDate, DateInstalled,
    //   SubDivisionName, CouncilDistrict, Comment
    TrafficSign: {
        alwaysVisible: [
            'ClientSignID',
            'StreetName',
            'SignClassification',
            'SignText',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['CardinalDirection', 'SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: [
                    'MUTCDCode', 'PanelHeight', 'PanelWidth', 'TextHeight',
                    'MountingHeight', 'SupportType', 'SupportMaterial',
                    'SignBacking', 'SheetingType', 'SignManufacturer',
                    'SignManufacturedDate', 'DateInstalled',
                ],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },

    // ── WaterMeter ───────────────────────────────────────
    // display:true fields: ClientWaterMeterID, Address, MeterNumber, AccountNumber,
    //   MeterType, MeterManufacturer, DateInstalled, SubDivisionName,
    //   CouncilDistrict, Comment
    WaterMeter: {
        alwaysVisible: [
            'ClientWaterMeterID',
            'Address',
            'MeterNumber',
            'AccountNumber',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: ['MeterType', 'MeterManufacturer', 'DateInstalled'],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },

    // ── WaterLine ────────────────────────────────────────
    // display:true fields: ClientWaterLineID, NetworkID, WaterLineManufacturer,
    //   WaterLineManufacturedDate, BeginningStation, EndingStation, LineDiameter,
    //   WaterLineMaterial, SubDivisionName, CouncilDistrict, DateInstalled, Comment
    WaterLine: {
        alwaysVisible: [
            'ClientWaterLineID',
            'WaterLineMaterial',
            'LineDiameter',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: [
                    'NetworkID', 'WaterLineManufacturer', 'WaterLineManufacturedDate',
                    'BeginningStation', 'EndingStation', 'DateInstalled',
                ],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },

    // ── SewerLine ────────────────────────────────────────
    // display:true fields: ClientSewerLineID, NetworkID, SewerLineManufacturer,
    //   SewerLineManufacturedDate, BeginningStation, EndingStation, LineDiameter,
    //   SewerLineMaterial, SubDivisionName, CouncilDistrict, DateInstalled, Comment
    SewerLine: {
        alwaysVisible: [
            'ClientSewerLineID',
            'SewerLineMaterial',
            'LineDiameter',
        ],
        sections: [
            {
                label: 'Location Details',
                fields: ['SubDivisionName', 'CouncilDistrict'],
            },
            {
                label: 'Technical Details',
                fields: [
                    'NetworkID', 'SewerLineManufacturer', 'SewerLineManufacturedDate',
                    'BeginningStation', 'EndingStation', 'DateInstalled',
                ],
            },
            {
                label: 'Notes',
                fields: ['Comment'],
            },
        ],
    },
};

export default FIELD_GROUPS;
